import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import { createPcmStreamer } from '../utils/pcmWorklet';
import CostTracker from '../components/CostTracker';
import { DEFAULT_SETTINGS, loadSettings, persistSettings } from '../utils/settings';
import { openDictationToolbar, isDocumentPictureInPictureSupported } from '../utils/pipToolbar';

const DEVICE_ID_STORAGE_KEY = 'wyrmDeviceId';
const TRANSCRIPTION_HISTORY_LIMIT = 200;
const HISTORY_COALESCE_WINDOW_MS = 1500;

const ensureDeviceId = () => {
  if (typeof window === 'undefined') {
    return `device-${Math.random().toString(16).slice(2)}`;
  }
  try {
    const storage = window.localStorage;
    let id = storage?.getItem(DEVICE_ID_STORAGE_KEY);
    if (!id) {
      if (window.crypto?.randomUUID) {
        id = window.crypto.randomUUID();
      } else {
        id = `device-${Date.now().toString(16)}-${Math.floor(Math.random() * 1e9).toString(16)}`;
      }
      storage?.setItem(DEVICE_ID_STORAGE_KEY, id);
    }
    return id;
  } catch (error) {
    return `device-${Date.now().toString(16)}-${Math.floor(Math.random() * 1e6).toString(16)}`;
  }
};

const detectBrowserName = (userAgent) => {
  if (!userAgent) return 'Browser';
  if (userAgent.includes('Edg/')) return 'Edge';
  if (userAgent.includes('OPR/') || userAgent.includes('Opera')) return 'Opera';
  if (userAgent.includes('Firefox/')) return 'Firefox';
  if (userAgent.includes('Safari/') && userAgent.includes('Version/')) return 'Safari';
  if (userAgent.includes('Chrome/')) return 'Chrome';
  if (userAgent.includes('MSIE') || userAgent.includes('Trident/')) return 'IE';
  return 'Browser';
};

const buildDeviceMetadata = (deviceId) => {
  if (typeof window === 'undefined') {
    return { deviceId, label: 'Device', platform: 'Unknown' };
  }
  const ua = window.navigator?.userAgent || '';
  const platform = window.navigator?.userAgentData?.platform || window.navigator?.platform || '';
  const browser = detectBrowserName(ua);
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
  const deviceType = isMobile ? 'Mobile' : 'Desktop';
  const labelParts = [deviceType, browser].filter(Boolean);
  const label = labelParts.join(' ');
  const isStandalone =
    !!window.matchMedia?.('(display-mode: standalone)').matches || !!window.navigator?.standalone;
  return {
    deviceId,
    label: label || platform || browser || deviceType || 'Device',
    platform,
    browser,
    userAgent: ua,
    isStandalone,
    deviceType,
  };
};

const summarisePeer = (peer) => {
  if (!peer) return 'Device';
  return (
    peer.label ||
    peer.platform ||
    peer.browser ||
    (peer.deviceId ? `Device ${String(peer.deviceId).slice(-4)}` : undefined) ||
    (peer.connectionId ? `Device ${String(peer.connectionId).slice(-4)}` : 'Device')
  );
};

const MicIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
  </svg>
);

const StopIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 10a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
  </svg>
);

const DocumentIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const COST_RATES = {
  azure: {
    realtime: 0.773,
    batch: 0.616,
  },
  speechmatics: {
    realtime: 0.9,
    batch: 0.72,
  },
  sherpa: {
    realtime: 0.0,
    batch: 0.0,
  },
};

const DEFAULT_RECORDING_SECONDS = Number(process.env.REACT_APP_RECORDING_LIMIT_SECONDS || 120);
const SYNC_INITIAL_RECONNECT_DELAY_MS = 1500;
const SYNC_MAX_RECONNECT_DELAY_MS = 60000;
const SYNC_FAILURE_BACKOFF_THRESHOLD = 3;
const SYNC_FAILURE_PAUSE_DELAY_MS = 5 * 60 * 1000;

const CipherPage = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const [mode, setMode] = useState(DEFAULT_SETTINGS.defaultMode);
  const [isRecording, setIsRecording] = useState(false);
  const [transcriptionText, setTranscriptionTextState] = useState('');
  const [prompts, setPrompts] = useState([]);
  const [stats, setStats] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCorrecting, setIsCorrecting] = useState(false);
  const [isCorrectionDialogOpen, setIsCorrectionDialogOpen] = useState(false);
  const [instructionDraft, setInstructionDraft] = useState('');
  const [confidence, setConfidence] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [sessionCost, setSessionCost] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [remainingTime, setRemainingTime] = useState(DEFAULT_RECORDING_SECONDS);
  const [inputLevel, setInputLevel] = useState(0);
  const [isSilencePaused, setIsSilencePaused] = useState(false);
  const [silencePauseMessage, setSilencePauseMessage] = useState('');
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth <= 768;
  });
  const [clientSettings, setClientSettings] = useState(DEFAULT_SETTINGS);
  const [isDocPipAvailable, setIsDocPipAvailable] = useState(() => {
    if (typeof window === 'undefined') return false;
    return isDocumentPictureInPictureSupported();
  });
  const settingsRef = useRef(DEFAULT_SETTINGS);

  const deviceIdRef = useRef(
    typeof window !== 'undefined'
      ? ensureDeviceId()
      : `device-${Math.random().toString(16).slice(2)}`
  );
  const deviceMetadataRef = useRef(
    typeof window !== 'undefined'
      ? buildDeviceMetadata(deviceIdRef.current)
      : { deviceId: deviceIdRef.current, label: 'Device', platform: 'Unknown' }
  );
  const [syncPeers, setSyncPeers] = useState([]);
  const [syncConnectionId, setSyncConnectionId] = useState(null);
  const [isSyncConnected, setIsSyncConnected] = useState(false);
  const wsRef = useRef(null);
  const pendingFramesRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const levelRafRef = useRef(null);
  const pcmStreamerRef = useRef(null);
  const batchAbortControllerRef = useRef(null);
  const awaitingCompleteRef = useRef(false);
  const clearOnNextRecordingRef = useRef(false);
  const countdownRef = useRef(null);
  const costIntervalRef = useRef(null);
  const autoStopRef = useRef(false);
  const stopRecordingRef = useRef(null);
  const syncWsRef = useRef(null);
  const syncReadyRef = useRef(false);
  const syncReconnectTimerRef = useRef(null);
  const syncReconnectDelayRef = useRef(SYNC_INITIAL_RECONNECT_DELAY_MS);
  const syncConnectionAttemptOpenedRef = useRef(false);
  const syncOpenFailureCountRef = useRef(0);
  const syncVersionRef = useRef(0);
  const skipSyncBroadcastRef = useRef(false);
  const lastBroadcastTextRef = useRef('');
  const lastSyncedTextRef = useRef('');
  const lastSyncedVersionRef = useRef(0);
  const broadcastTimeoutRef = useRef(null);
  const transcriptionRef = useRef('');
  const transcriptionTextareaRef = useRef(null);
  const transcriptionHistoryRef = useRef({
    past: [],
    future: [],
    lastCoalesceKey: null,
    lastPushAt: 0,
    isNavigating: false,
    lastValue: '',
  });
  const pipWindowRef = useRef(null);
  const syncAuthFailureRef = useRef(false);
  const syncConnectionIdRef = useRef(null);
  const instructionInputRef = useRef(null);
  const lastInstructionRef = useRef('');

  const currentProvider = clientSettings.sttProvider || 'azure';
  const providerRates = COST_RATES[currentProvider] || COST_RATES.azure;
  const DEBUG = process.env.REACT_APP_DEBUG === '1' || process.env.REACT_APP_DEBUG === 'true';

  const setTranscriptionText = useCallback(
    (valueOrUpdater, options = {}) => {
      const { skipHistory = false, coalesceKey = null } = options;
      setTranscriptionTextState((prev) => {
        const nextRaw = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
        const nextValue =
          typeof nextRaw === 'string' ? nextRaw : nextRaw == null ? '' : String(nextRaw);

        if (nextValue === prev) {
          return prev;
        }

        const history = transcriptionHistoryRef.current;

        if (!skipHistory && !history.isNavigating) {
          const now = Date.now();
          const canCoalesce =
            coalesceKey &&
            history.lastCoalesceKey === coalesceKey &&
            history.past.length > 0 &&
            now - history.lastPushAt <= HISTORY_COALESCE_WINDOW_MS;

          if (!canCoalesce) {
            const nextPast = [...history.past, prev];
            if (nextPast.length > TRANSCRIPTION_HISTORY_LIMIT) {
              nextPast.splice(0, nextPast.length - TRANSCRIPTION_HISTORY_LIMIT);
            }
            history.past = nextPast;
            history.lastCoalesceKey = coalesceKey || null;
            history.lastPushAt = now;
          } else {
            history.lastPushAt = now;
          }
          history.future = [];
        }

        history.lastValue = nextValue;
        return nextValue;
      });
    },
    [setTranscriptionTextState]
  );

  const undoTranscription = useCallback(() => {
    const history = transcriptionHistoryRef.current;
    if (!history.past.length) {
      return false;
    }

    const previousValue = history.past[history.past.length - 1];
    const currentValue = transcriptionRef.current || '';

    history.past = history.past.slice(0, -1);
    const nextFuture = [currentValue, ...history.future];
    if (nextFuture.length > TRANSCRIPTION_HISTORY_LIMIT) {
      nextFuture.splice(TRANSCRIPTION_HISTORY_LIMIT);
    }
    history.future = nextFuture;
    history.isNavigating = true;
    setTranscriptionText(previousValue, { skipHistory: true });
    history.isNavigating = false;
    history.lastCoalesceKey = null;
    history.lastPushAt = Date.now();
    return true;
  }, [setTranscriptionText]);

  const redoTranscription = useCallback(() => {
    const history = transcriptionHistoryRef.current;
    if (!history.future.length) {
      return false;
    }

    const nextValue = history.future[0];
    const currentValue = transcriptionRef.current || '';

    history.future = history.future.slice(1);
    const nextPast = [...history.past, currentValue];
    if (nextPast.length > TRANSCRIPTION_HISTORY_LIMIT) {
      nextPast.splice(0, nextPast.length - TRANSCRIPTION_HISTORY_LIMIT);
    }
    history.past = nextPast;
    history.isNavigating = true;
    setTranscriptionText(nextValue, { skipHistory: true });
    history.isNavigating = false;
    history.lastCoalesceKey = null;
    history.lastPushAt = Date.now();
    return true;
  }, [setTranscriptionText]);

  const updateToolbarCopyState = useCallback(() => {
    const pipWindow = pipWindowRef.current;
    if (!pipWindow || pipWindow.closed) {
      return;
    }
    const { copyBtn } = pipWindow.__toolbarElements || {};
    if (copyBtn) {
      const hasText = Boolean(transcriptionRef.current && transcriptionRef.current.trim());
      copyBtn.disabled = !hasText;
      copyBtn.title = hasText ? 'Copy transcript' : 'Nothing to copy yet';
    }
  }, []);

  const copyTranscriptionToClipboard = useCallback(
    async ({
      successMessage = 'Copied to clipboard',
      silentOnEmpty = false,
      navigatorOverride,
    } = {}) => {
      const text = transcriptionRef.current || '';
      const hasText = Boolean(text && text.length);
      if (!hasText) {
        if (!silentOnEmpty) {
          toast.error('Nothing to copy yet');
        }
        return false;
      }

      const globalNavigator = typeof navigator !== 'undefined' ? navigator : null;
      const activeNavigator = navigatorOverride || globalNavigator;

      if (
        !activeNavigator ||
        !activeNavigator.clipboard ||
        typeof activeNavigator.clipboard.writeText !== 'function'
      ) {
        toast.error('Clipboard API unavailable');
        return false;
      }
      try {
        await activeNavigator.clipboard.writeText(text);
        if (successMessage) {
          toast.success(successMessage);
        }
        clearOnNextRecordingRef.current = true;
        return true;
      } catch (error) {
        console.error('Clipboard write failed', error);
        toast.error('Unable to copy to clipboard');
        return false;
      }
    },
    []
  );

  const handleAiCorrection = useCallback(async (instructions = '') => {
    const currentText = transcriptionRef.current || '';
    if (!currentText.trim()) {
      toast.error('Nothing to correct yet');
      return;
    }

    const trimmedInstructions = (instructions || '').trim();
    lastInstructionRef.current = trimmedInstructions;

    setIsCorrecting(true);
    const toastId = toast.loading('Applying AI correction...');

    try {
      const { data } = await api.post('/api/stt/corrections/ai', {
        text: currentText,
        instructions: trimmedInstructions,
      });
      const corrected = typeof data?.corrected_text === 'string' ? data.corrected_text : null;

      if (corrected === null) {
        toast.error('AI correction did not return any text', { id: toastId });
        return;
      }

      setTranscriptionText(corrected);
      transcriptionRef.current = corrected;
      setConfidence(null);

      toast.success('AI correction applied', { id: toastId });
    } catch (error) {
      console.error('AI correction failed', error);
      const detail =
        error?.response?.data?.detail ||
        error?.message ||
        'Unable to apply AI correction. Please try again.';
      toast.error(detail, { id: toastId });
    } finally {
      setIsCorrecting(false);
    }
  }, [setConfidence, setIsCorrecting, setTranscriptionText]);

  const openAiCorrectionDialog = useCallback(() => {
    if (isCorrectionDialogOpen) {
      return;
    }
    const hasText = Boolean(transcriptionRef.current && transcriptionRef.current.trim());
    if (!hasText) {
      toast.error('Nothing to correct yet');
      return;
    }
    setInstructionDraft(lastInstructionRef.current || '');
    setIsCorrectionDialogOpen(true);
  }, [isCorrectionDialogOpen, setInstructionDraft, setIsCorrectionDialogOpen]);

  const closeAiCorrectionDialog = useCallback(() => {
    setIsCorrectionDialogOpen(false);
  }, []);

  const handleConfirmCorrection = useCallback(() => {
    if (isCorrecting) {
      return;
    }
    const instructionsToSend = instructionDraft.trim();
    setIsCorrectionDialogOpen(false);
    handleAiCorrection(instructionsToSend);
  }, [handleAiCorrection, instructionDraft, isCorrecting, setIsCorrectionDialogOpen]);

  useEffect(() => {
    if (isCorrectionDialogOpen && instructionInputRef.current) {
      instructionInputRef.current.focus();
      instructionInputRef.current.select();
    }
  }, [isCorrectionDialogOpen]);

  useEffect(() => {
    if (!isCorrectionDialogOpen) {
      return;
    }
    const handleDialogKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        setIsCorrectionDialogOpen(false);
      } else if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'enter') {
        event.preventDefault();
        handleConfirmCorrection();
      }
    };
    document.addEventListener('keydown', handleDialogKeyDown);
    return () => {
      document.removeEventListener('keydown', handleDialogKeyDown);
    };
  }, [handleConfirmCorrection, isCorrectionDialogOpen, setIsCorrectionDialogOpen]);

  const focusMainWindow = useCallback((/* options */) => {
    if (typeof window === 'undefined') return;
    try {
      window.focus();
    } catch (err) {
      if (DEBUG) console.warn('[Toolbar] focus failed', err);
    }
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      window.dispatchEvent(new Event('cipher:focus-request'));
    }
  }, [DEBUG]);

  const handleOpenToolbar = useCallback(async () => {
    if (!isDocumentPictureInPictureSupported()) {
      setIsDocPipAvailable(false);
      toast.error('Floating toolbar requires a browser with Document Picture-in-Picture support.');
      return;
    }
    if (pipWindowRef.current && !pipWindowRef.current.closed) {
      pipWindowRef.current.focus();
      return;
    }
    try {
      const pipWindow = await openDictationToolbar(
        ({ pipWindow: toolbarWindow }) =>
          copyTranscriptionToClipboard({
            successMessage: 'Transcript copied to clipboard',
            navigatorOverride: toolbarWindow?.navigator,
          }),
        focusMainWindow,
        {
          onClose: () => {
            pipWindowRef.current = null;
          },
        }
      );
      pipWindowRef.current = pipWindow;
      updateToolbarCopyState();
    } catch (error) {
      console.error('Failed to open floating toolbar', error);
      toast.error('Unable to open floating toolbar');
    }
  }, [copyTranscriptionToClipboard, focusMainWindow, updateToolbarCopyState, setIsDocPipAvailable]);

  const handleSyncAuthFailure = useCallback(() => {
    if (syncAuthFailureRef.current) {
      return;
    }
    syncAuthFailureRef.current = true;
    toast.error('Session expired. Please sign in again.');
    if (typeof logout === 'function') {
      Promise.resolve(logout()).catch((err) => {
        console.error('Logout after sync auth failure failed', err);
        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }
      });
    } else if (typeof window !== 'undefined') {
      window.location.href = '/login';
    }
  }, [logout]);

  useEffect(() => {
    transcriptionRef.current = transcriptionText;
    updateToolbarCopyState();
  }, [transcriptionText, updateToolbarCopyState]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    setIsDocPipAvailable(isDocumentPictureInPictureSupported());
  }, []);

  useEffect(() => {
    return () => {
      if (pipWindowRef.current && !pipWindowRef.current.closed) {
        pipWindowRef.current.close();
      }
      pipWindowRef.current = null;
    };
  }, []);

  // Track viewport size for responsive styles
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const stored = loadSettings();
    setClientSettings(stored);
    settingsRef.current = stored;
    setMode(stored.defaultMode || DEFAULT_SETTINGS.defaultMode);
  }, []);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'wyrmSettings') {
        const updated = loadSettings();
        setClientSettings(updated);
        settingsRef.current = updated;
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const handleModeChange = useCallback((newMode) => {
    setMode(newMode);
    const current = loadSettings();
    const updated = { ...current, defaultMode: newMode };
    setClientSettings(updated);
    settingsRef.current = updated;
    persistSettings(updated);
  }, []);

  const handleProviderChange = useCallback((newProvider) => {
    const current = loadSettings();
    const updated = { ...current, sttProvider: newProvider };
    setClientSettings(updated);
    settingsRef.current = updated;
    persistSettings(updated);
    toast.success(
      `Switched to ${
        newProvider === 'sherpa'
          ? 'Sherpa ONNX'
          : newProvider === 'speechmatics'
          ? 'Speechmatics'
          : 'Azure Speech'
      }`
    );
  }, []);

  useEffect(() => {
    return () => {
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (typeof window !== 'undefined') {
      deviceMetadataRef.current = buildDeviceMetadata(deviceIdRef.current);
    }

    const resetSyncState = (allowStateUpdates = true) => {
      syncReadyRef.current = false;
      if (allowStateUpdates) {
        setIsSyncConnected(false);
        setSyncConnectionId(null);
        setSyncPeers([]);
      }
    };

    const teardown = () => {
      resetSyncState(!cancelled);
      if (syncReconnectTimerRef.current) {
        clearTimeout(syncReconnectTimerRef.current);
        syncReconnectTimerRef.current = null;
      }
      syncReconnectDelayRef.current = SYNC_INITIAL_RECONNECT_DELAY_MS;
      if (broadcastTimeoutRef.current) {
        clearTimeout(broadcastTimeoutRef.current);
        broadcastTimeoutRef.current = null;
      }
      const existing = syncWsRef.current;
      if (existing) {
        try {
          existing.close();
        } catch (err) {
          if (DEBUG) console.error('[SYNC] close error', err);
        }
        syncWsRef.current = null;
      }
    };

    if (authLoading || !user) {
      teardown();
      return () => { };
    }

    syncAuthFailureRef.current = false;
    syncReconnectDelayRef.current = SYNC_INITIAL_RECONNECT_DELAY_MS;

    const envSync = process.env.REACT_APP_SYNC_WS_URL && process.env.REACT_APP_SYNC_WS_URL.trim();
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const defaultUrl = `${protocol}//${window.location.host}/api/sync/text`;
    const wsUrl = envSync && envSync !== '' ? envSync : defaultUrl;

    const connect = () => {
      if (cancelled) {
        return;
      }

      if (syncAuthFailureRef.current) {
        return;
      }

      if (syncWsRef.current && syncWsRef.current.readyState === WebSocket.OPEN) {
        return;
      }

      syncConnectionAttemptOpenedRef.current = false;
      const socket = new WebSocket(wsUrl);
      syncWsRef.current = socket;
      syncVersionRef.current = 0;

      socket.onopen = () => {
        syncConnectionAttemptOpenedRef.current = true;
        syncOpenFailureCountRef.current = 0;
        syncReadyRef.current = true;
        setIsSyncConnected(true);
        syncReconnectDelayRef.current = SYNC_INITIAL_RECONNECT_DELAY_MS;
        if (DEBUG) console.log('[SYNC] connected');
        try {
          socket.send(JSON.stringify({ type: 'request_state' }));
        } catch (err) {
          if (DEBUG) console.error('[SYNC] failed to request state', err);
        }

        try {
          socket.send(JSON.stringify({ type: 'identify', ...deviceMetadataRef.current }));
        } catch (err) {
          if (DEBUG) console.error('[SYNC] failed to send identity', err);
        }

        const localText = transcriptionRef.current;
        if (localText) {
          try {
            lastBroadcastTextRef.current = localText;
            socket.send(
              JSON.stringify({
                type: 'update',
                text: localText,
                clientVersion: syncVersionRef.current,
              })
            );
          } catch (err) {
            if (DEBUG) console.error('[SYNC] failed to send initial text', err);
          }
        }
      };

      socket.onmessage = (event) => {
        let data;
        try {
          data = JSON.parse(event.data);
        } catch (err) {
          if (DEBUG) console.error('[SYNC] invalid payload', err);
          return;
        }

        if (data.type === 'identity_ack') {
          if (typeof data.connectionId === 'string') {
            setSyncConnectionId(data.connectionId);
            syncConnectionIdRef.current = data.connectionId;
          }
          return;
        }

        if (data.type === 'peers') {
          const peers = Array.isArray(data.peers) ? data.peers : [];
          setSyncPeers(peers);
          return;
        }

        if (data.type !== 'state' && data.type !== 'update') {
          if (DEBUG) console.log('[SYNC] ignored message', data);
          return;
        }

        const incomingVersion = typeof data.version === 'number' ? data.version : 0;
        const incomingText = typeof data.text === 'string' ? data.text : '';
        const localText = transcriptionRef.current;
        const sourceConnectionId =
          typeof data.sourceConnectionId === 'string' ? data.sourceConnectionId : null;

        syncVersionRef.current = incomingVersion;
        lastSyncedTextRef.current = incomingText;
        lastSyncedVersionRef.current = incomingVersion;

        if (incomingVersion === 0 && incomingText === '' && localText) {
          lastBroadcastTextRef.current = localText;
          return;
        }

        lastBroadcastTextRef.current = incomingText;

        if (sourceConnectionId && sourceConnectionId === syncConnectionIdRef.current) {
          skipSyncBroadcastRef.current = false;
          return;
        }

        if (incomingText === localText) {
          skipSyncBroadcastRef.current = false;
          return;
        }

        skipSyncBroadcastRef.current = true;
        setTranscriptionText(incomingText);
      };

      socket.onclose = (event) => {
        if (DEBUG) console.log('[SYNC] disconnected', event.code, event.reason);
        if (syncWsRef.current === socket) {
          syncWsRef.current = null;
        }
        syncReadyRef.current = false;
        setIsSyncConnected(false);
        setSyncConnectionId(null);
        syncConnectionIdRef.current = null;
        setSyncPeers([]);

        const attemptOpened = syncConnectionAttemptOpenedRef.current;
        syncConnectionAttemptOpenedRef.current = false;

        if (event?.code === 4401 || event?.code === 4403) {
          handleSyncAuthFailure();
          return;
        }
        if (cancelled || syncAuthFailureRef.current) {
          return;
        }

        if (!attemptOpened) {
          syncOpenFailureCountRef.current = Math.min(
            syncOpenFailureCountRef.current + 1,
            SYNC_FAILURE_BACKOFF_THRESHOLD
          );
        } else {
          syncOpenFailureCountRef.current = 0;
        }

        const shouldPause =
          syncOpenFailureCountRef.current >= SYNC_FAILURE_BACKOFF_THRESHOLD;
        const baseDelay = Math.min(syncReconnectDelayRef.current, SYNC_MAX_RECONNECT_DELAY_MS);
        const delay = shouldPause ? SYNC_FAILURE_PAUSE_DELAY_MS : baseDelay;

        if (syncReconnectTimerRef.current) {
          clearTimeout(syncReconnectTimerRef.current);
        }
        syncReconnectTimerRef.current = setTimeout(() => {
          if (!cancelled && !syncAuthFailureRef.current) {
            connect();
          }
        }, delay);
        syncReconnectDelayRef.current = shouldPause
          ? SYNC_FAILURE_PAUSE_DELAY_MS
          : Math.min(baseDelay * 2, SYNC_MAX_RECONNECT_DELAY_MS);
      };

      socket.onerror = (err) => {
        if (DEBUG) console.error('[SYNC] error', err);
        socket.close();
      };
    };

    connect();

    return () => {
      cancelled = true;
      teardown();
    };
  }, [user, authLoading, DEBUG, handleSyncAuthFailure, setTranscriptionText]);

  useEffect(() => {
    if (broadcastTimeoutRef.current) {
      clearTimeout(broadcastTimeoutRef.current);
      broadcastTimeoutRef.current = null;
    }

    if (skipSyncBroadcastRef.current) {
      skipSyncBroadcastRef.current = false;
      return;
    }

    const socket = syncWsRef.current;
    if (!socket || socket.readyState !== WebSocket.OPEN || !syncReadyRef.current) {
      return;
    }

    const nextBroadcastText = transcriptionText;
    if (nextBroadcastText === lastBroadcastTextRef.current) {
      return;
    }

    if (
      lastSyncedTextRef.current === nextBroadcastText &&
      lastSyncedVersionRef.current === syncVersionRef.current
    ) {
      return;
    }

    const versionAtSchedule = syncVersionRef.current;
    broadcastTimeoutRef.current = setTimeout(() => {
      const activeSocket = syncWsRef.current;
      if (!activeSocket || activeSocket.readyState !== WebSocket.OPEN) {
        return;
      }
      const payload = {
        type: 'update',
        text: nextBroadcastText,
        clientVersion: versionAtSchedule,
      };
      lastBroadcastTextRef.current = nextBroadcastText;
      try {
        activeSocket.send(JSON.stringify(payload));
      } catch (error) {
        if (DEBUG) console.error('[SYNC] Failed to send update', error);
      }
    }, 150);

    return () => {
      if (broadcastTimeoutRef.current) {
        clearTimeout(broadcastTimeoutRef.current);
        broadcastTimeoutRef.current = null;
      }
    };
  }, [transcriptionText, DEBUG]);

  const loadPrompts = useCallback(async () => {
    try {
      const response = await api.get('/api/stt/prompts');
      setPrompts(response.data);
    } catch (error) {
      console.error('Failed to load prompts:', error);
    }
  }, []);

  const loadStats = useCallback(async () => {
    try {
      const response = await api.get('/api/stt/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  }, []);

  const loadCosts = useCallback(async () => {
    try {
      const [sessionRes, totalRes] = await Promise.all([
        api.get('/api/stt/costs/session'),
        api.get('/api/stt/costs/total')
      ]);
      setSessionCost(sessionRes.data.session_cost_gbp || 0);
      setTotalCost(totalRes.data.total_cost_gbp || 0);
    } catch (error) {
      console.error('Failed to load costs:', error);
    }
  }, []);

  const finalizeSessionCost = useCallback(
    async (elapsedMs, sessionIdentifier, sessionMode) => {
      if (!sessionIdentifier || !elapsedMs) {
        return;
      }
      const durationSeconds = elapsedMs / 1000;
      const effectiveMode = sessionMode || 'realtime';
      const provider = settingsRef.current?.sttProvider || 'azure';
      const providerRates = COST_RATES[provider] || COST_RATES.azure;
      const ratePerHour = providerRates[effectiveMode] ?? providerRates.realtime;
      const estimated = (durationSeconds / 3600) * ratePerHour;
      setSessionCost(estimated);

      try {
        const { data } = await api.post('/api/stt/costs/track', {
          session_id: sessionIdentifier,
          mode: effectiveMode,
          duration_seconds: durationSeconds,
        });
        if (data && typeof data.total_cost_gbp === 'number') {
          setSessionCost(data.total_cost_gbp);
        }
        await loadCosts();
      } catch (error) {
        console.error('Failed to track session cost:', error);
      }
    },
    [loadCosts]
  );

  const startRealtimeRecording = (stream) => {
    // Compute WS URL: prefer explicit env, else same-origin path under /api
    const envWs = process.env.REACT_APP_WS_URL && process.env.REACT_APP_WS_URL.trim() !== ''
      ? process.env.REACT_APP_WS_URL.trim()
      : null;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const defaultWs = `${protocol}//${window.location.host}/api/stt/transcribe/realtime`;
    const provider = settingsRef.current?.sttProvider || 'azure';
    const baseWs = envWs || defaultWs;
    const wsUrl = baseWs.includes('?') ? `${baseWs}&provider=${provider}` : `${baseWs}?provider=${provider}`;

    if (DEBUG) console.log('[WS] Connecting to', wsUrl);
    wsRef.current = new WebSocket(wsUrl);
    wsRef.current.binaryType = 'arraybuffer';

    wsRef.current.onopen = () => {
      if (DEBUG) console.log('[WS] connected');
      const activeSettings = settingsRef.current;
      if (activeSettings) {
        try {
          wsRef.current.send(
            JSON.stringify({
              type: 'config',
              autoExpand:
                !!activeSettings.enableRealtimeCorrections && !!activeSettings.enableAutoExpand,
              overrides: activeSettings.autoExpandOverrides || {},
              provider: activeSettings.sttProvider || 'azure',
            })
          );
        } catch (error) {
          if (DEBUG) console.error('[WS] Failed to send config', error);
        }
      }
      // Flush any buffered audio frames captured before socket was open
      try {
        const buffered = pendingFramesRef.current;
        if (buffered && buffered.length) {
          for (const frame of buffered) {
            if (wsRef.current?.readyState === WebSocket.OPEN) {
              wsRef.current.send(frame);
            } else {
              break;
            }
          }
        }
      } finally {
        pendingFramesRef.current = [];
      }
    };

    wsRef.current.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.type === 'partial') {
        if (DEBUG) console.log('[WS] Partial:', data.text);
      } else if (data.type === 'final') {
        if (DEBUG) console.log('[WS] Final:', data.text);
        setTranscriptionText(prev => {
          // A smarter way to join text that handles spacing and punctuation
          let newText = prev;
          if (prev && !prev.endsWith(' ')) {
            newText += ' ';
          }
          newText += data.text;
          // Tidy up spaces around punctuation for a cleaner output
          newText = newText.replace(/ \./g, '.').replace(/ \?/g, '?').replace(/ ,/g, ',');
          return newText;
        });

        if (data.confidence !== null) {
          setConfidence(data.confidence);
        }
      } else if (data.type === 'paused_silence') {
        const pauseMessage =
          typeof data.message === 'string' && data.message.trim()
            ? data.message.trim()
            : 'Recording paused after silence. Start again to continue.';
        if (DEBUG) console.log('[WS] Paused for silence:', pauseMessage);
        setSilencePauseMessage(pauseMessage);
        setIsSilencePaused(true);
        if (isRecording) {
          void stopRecording({ reason: pauseMessage, keepPauseState: true });
        }
      } else if (data.type === 'complete') {
        console.log('[WS] Complete received');
        awaitingCompleteRef.current = false;
        toast.success('Transcription complete');
      } else if (data.type === 'corrections') {
        if (DEBUG) console.log('[WS] Corrections:', data.corrections || data.changes);
      } else if (data.type === 'config_ack') {
        if (DEBUG) console.log('[WS] Config ack:', data);
      } else if (data.error) {
        if (DEBUG) console.error('[WS] Error:', data.error);
        toast.error(`Error: ${data.error}`);
      }
    };

    wsRef.current.onerror = (error) => {
      if (DEBUG) console.error('[WS] error', error);
      toast.error('Connection error');
    };

    wsRef.current.onclose = (evt) => {
      if (DEBUG) console.log('[WS] close', { code: evt.code, reason: evt.reason });
    };

    // Setup audio visualization
    setupAudioVisualization(stream);

    // Stream PCM audio data to WebSocket (buffer until socket opens)
    createPcmStreamer(stream, {
      audioContext: audioContextRef.current,
      onFrame: (frame) => {
        // frame is Uint8Array of little-endian PCM bytes
        const bytes = frame instanceof Uint8Array ? frame : new Uint8Array(frame.buffer || frame);
        const ready = wsRef.current?.readyState === WebSocket.OPEN;
        if (ready) {
          // Flush any buffered frames first
          if (pendingFramesRef.current.length) {
            for (const frame of pendingFramesRef.current) {
              wsRef.current.send(frame);
            }
            pendingFramesRef.current = [];
          }
          wsRef.current.send(bytes);
        } else {
          // Keep a small buffer of initial frames to avoid Azure timeout
          if (pendingFramesRef.current.length < 200) { // ~25s of buffered audio
            pendingFramesRef.current.push(bytes);
          } else {
            // Drop if buffer is full to avoid unbounded growth
            pendingFramesRef.current.shift();
            pendingFramesRef.current.push(bytes);
          }
        }
      },
      targetSampleRate: 16000
    }).then((streamer) => {
      pcmStreamerRef.current = streamer;
    });
  };

  const startBatchRecording = (stream) => {
    // Prefer an explicit mimeType if available; fall back to browser default
    let options = {};
    // Try to pick a widely supported container with Opus
    if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
      options.mimeType = 'audio/webm;codecs=opus';
    } else if (window.MediaRecorder && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')) {
      options.mimeType = 'audio/ogg;codecs=opus';
    }
    const mediaRecorder = new MediaRecorder(stream, options);
    mediaRecorderRef.current = mediaRecorder;
    audioChunksRef.current = [];

    setupAudioVisualization(stream);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onstop = async () => {
      const blobType = mediaRecorder.mimeType || (audioChunksRef.current[0] && audioChunksRef.current[0].type) || 'audio/webm';
      const audioBlob = new Blob(audioChunksRef.current, { type: blobType });
      await sendBatchAudio(audioBlob, blobType);
    };

    mediaRecorder.start();
  };

  const setupAudioVisualization = (stream) => {
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyserRef.current = analyser;
      source.connect(analyser);

      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      const updateLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        setInputLevel(average / 255);
        levelRafRef.current = requestAnimationFrame(updateLevel);
      };

      updateLevel();
    } catch (e) {
      console.warn('Audio visualization failed:', e);
    }
  };

  const stopRecording = useCallback(
    async ({ autoStop = false, reason, keepPauseState = false } = {}) => {
      if (!keepPauseState) {
        setIsSilencePaused(false);
        setSilencePauseMessage('');
      }
      if (batchAbortControllerRef.current) {
        batchAbortControllerRef.current.abort();
        batchAbortControllerRef.current = null;
      }
      if (!isRecording && !mediaRecorderRef.current && !wsRef.current) {
        return;
      }

      const currentMode = mode;
      autoStopRef.current = false;

      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }

      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }

      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        awaitingCompleteRef.current = true;
        wsRef.current.send('stop');
        setTimeout(() => {
          if (awaitingCompleteRef.current && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.close();
          }
        }, 1500);
      }
      pendingFramesRef.current = [];

      const elapsedMs = sessionStartTime ? Date.now() - sessionStartTime : 0;
      const currentSessionId = sessionId;

      setIsRecording(false);
      setSessionStartTime(null);
      setSessionId(null);
      setRemainingTime(DEFAULT_RECORDING_SECONDS);

      if (costIntervalRef.current) {
        clearInterval(costIntervalRef.current);
        costIntervalRef.current = null;
      }

      const toastMessage = reason
        ? reason
        : autoStop
          ? 'Recording stopped (time limit reached)'
          : 'Recording stopped';
      toast.success(toastMessage);
      loadStats();

      if (levelRafRef.current) cancelAnimationFrame(levelRafRef.current);
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
      setInputLevel(0);

      if (pcmStreamerRef.current) {
        pcmStreamerRef.current.close();
        pcmStreamerRef.current = null;
      }

      if (awaitingCompleteRef.current) {
        awaitingCompleteRef.current = false;
      }

      if (elapsedMs > 0 && currentSessionId) {
        await finalizeSessionCost(elapsedMs, currentSessionId, currentMode);
      }
    },
    [finalizeSessionCost, isRecording, loadStats, mode, sessionId, sessionStartTime]
  );

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      setIsSilencePaused(false);
      setSilencePauseMessage('');

      if (clearOnNextRecordingRef.current && transcriptionRef.current) {
        clearOnNextRecordingRef.current = false;
        transcriptionRef.current = '';
        setTranscriptionText('');
        setConfidence(null);
      } else if (clearOnNextRecordingRef.current) {
        clearOnNextRecordingRef.current = false;
      }

      const newSessionId = `session-${Date.now()}`;
      setSessionId(newSessionId);
      setSessionStartTime(Date.now());
      setSessionCost(0);
      setRemainingTime(DEFAULT_RECORDING_SECONDS);
      autoStopRef.current = false;

      if (mode === 'realtime') {
        startRealtimeRecording(stream);
      } else {
        startBatchRecording(stream);
      }

      setIsRecording(true);
      toast.success(`Recording started (${mode} mode)`);

      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
      countdownRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            if (!autoStopRef.current) {
              autoStopRef.current = true;
              if (countdownRef.current) {
                clearInterval(countdownRef.current);
                countdownRef.current = null;
              }
              stopRecordingRef.current?.({ autoStop: true });
            }
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      toast.error('Failed to access microphone. Please check permissions.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const formatCountdown = (seconds) => {
    const clamped = Math.max(0, Math.round(seconds));
    const mins = String(Math.floor(clamped / 60)).padStart(2, '0');
    const secs = String(clamped % 60).padStart(2, '0');
    return `${mins}:${secs}`;
  };

  const handleCountdownReset = () => {
    setRemainingTime(DEFAULT_RECORDING_SECONDS);
    autoStopRef.current = false;
  };

  useEffect(() => {
    stopRecordingRef.current = stopRecording;
  }, [stopRecording]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    const handleVisibilityLoss = (event) => {
      const becameHidden =
        event.type === 'visibilitychange' ? document.hidden : true;

      if (!becameHidden || !isRecording) {
        return;
      }

      stopRecordingRef.current?.({
        reason: 'Recording stopped because the app was hidden',
      });
    };

    document.addEventListener('visibilitychange', handleVisibilityLoss);
    window.addEventListener('pagehide', handleVisibilityLoss);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityLoss);
      window.removeEventListener('pagehide', handleVisibilityLoss);
    };
  }, [isRecording]);

  useEffect(() => {
    loadPrompts();
    loadStats();
    loadCosts();
  }, [loadPrompts, loadStats, loadCosts]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      const key = event.key?.toLowerCase();
      if (!key) {
        return;
      }

      const hasUndoModifier = event.ctrlKey || event.metaKey || event.altKey;
      const isUndoCombo = key === 'z' && hasUndoModifier && !event.shiftKey;
      const isRedoCombo =
        (key === 'y' && hasUndoModifier) ||
        (key === 'z' && (event.ctrlKey || event.metaKey) && event.shiftKey);

      if (isUndoCombo || isRedoCombo) {
        const activeElement = document.activeElement;
        const isTranscriptionFocused =
          !activeElement ||
          activeElement === document.body ||
          activeElement === transcriptionTextareaRef.current;

        if (!isTranscriptionFocused) {
          return;
        }

        if (isUndoCombo) {
          if (undoTranscription()) {
            event.preventDefault();
          }
          return;
        }

        if (redoTranscription()) {
          event.preventDefault();
        }
        return;
      }

      if (!event.ctrlKey && !event.metaKey) {
        return;
      }

      if (isCorrectionDialogOpen) {
        return;
      }

      if (key === 'd') {
        event.preventDefault();
        if (isRecording) {
          stopRecording();
        } else {
          startRecording();
        }
      } else if (key === 'k') {
        if (isCorrecting) {
          return;
        }
        event.preventDefault();
        openAiCorrectionDialog();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isCorrectionDialogOpen,
    isCorrecting,
    isRecording,
    openAiCorrectionDialog,
    redoTranscription,
    startRecording,
    stopRecording,
    transcriptionTextareaRef,
    undoTranscription,
  ]);

  useEffect(() => {
    if (!isRecording || !sessionStartTime) {
      if (costIntervalRef.current) {
        clearInterval(costIntervalRef.current);
        costIntervalRef.current = null;
      }
      return;
    }

    const ratePerHour = COST_RATES[mode] ?? COST_RATES.realtime;
    const updateCost = () => {
      const elapsed = Date.now() - sessionStartTime;
      if (elapsed >= 0) {
        setSessionCost((elapsed / 3600000) * ratePerHour);
      }
    };

    updateCost();
    costIntervalRef.current = setInterval(updateCost, 1000);

    return () => {
      if (costIntervalRef.current) {
        clearInterval(costIntervalRef.current);
        costIntervalRef.current = null;
      }
    };
  }, [isRecording, mode, sessionStartTime]);

  useEffect(() => {
    if (!isRecording) {
      setRemainingTime(DEFAULT_RECORDING_SECONDS);
    }
  }, [isRecording, mode]);

  const sendBatchAudio = async (audioBlob, blobType) => {
    setIsProcessing(true);
    const controller = new AbortController();
    batchAbortControllerRef.current = controller;
    const formData = new FormData();
    const type = blobType || audioBlob.type || 'audio/webm';
    const ext = type.includes('ogg') ? 'ogg' : type.includes('webm') ? 'webm' : (type.includes('mp3') || type.includes('mpeg')) ? 'mp3' : 'wav';
    formData.append('file', audioBlob, `recording.${ext}`);

    try {
      const response = await api.post('/api/stt/transcribe/batch', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        signal: controller.signal,
      });

      setTranscriptionText(prev => {
        const newText = prev ? prev + ' ' + response.data.text : response.data.text;
        return newText;
      });

      if (response.data.confidence) {
        setConfidence(response.data.confidence);
      }

      toast.success('Transcription complete');
    } catch (error) {
      if (error?.code === 'ERR_CANCELED') {
        if (DEBUG) console.log('Batch transcription aborted by user');
        return;
      }
      console.error('Batch transcription failed:', error);
      toast.error('Failed to transcribe audio');
    } finally {
      setIsProcessing(false);
      batchAbortControllerRef.current = null;
    }
  };

  const canShowSyncStatus = Boolean(user);
  const isPeerSelf = (peer) => {
    if (!peer) return false;
    if (syncConnectionId && peer.connectionId === syncConnectionId) {
      return true;
    }
    if (peer.deviceId && peer.deviceId === deviceIdRef.current) {
      return true;
    }
    return false;
  };
  const otherPeers = canShowSyncStatus ? syncPeers.filter((peer) => !isPeerSelf(peer)) : [];
  const otherCount = otherPeers.length;
  const peerNames = otherPeers
    .slice(0, 2)
    .map((peer) => summarisePeer(peer))
    .filter(Boolean);
  const remainingPeerCount = Math.max(0, otherCount - peerNames.length);

  let syncStatusLabel = '';
  if (canShowSyncStatus) {
    if (!isSyncConnected) {
      syncStatusLabel = 'Sync: connecting…';
    } else if (otherCount === 0) {
      syncStatusLabel = 'Sync: this device only';
    } else {
      const plural = otherCount === 1 ? 'device' : 'devices';
      syncStatusLabel = `Sync: ${otherCount} other ${plural}`;
    }
    if (otherCount > 0 && peerNames.length > 0) {
      const suffix = remainingPeerCount > 0 ? ` +${remainingPeerCount} more` : '';
      syncStatusLabel += ` (${peerNames.join(', ')}${suffix})`;
    }
  }

  const syncStatusClass = !isSyncConnected
    ? 'text-amber-600'
    : otherCount > 0
      ? 'text-emerald-600'
      : 'text-gray-500';

  const renderConfigCard = () => (
    <div className="card space-y-4">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <SettingsIcon />
        Configuration
      </h3>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Provider
          </label>
          <select
            value={clientSettings.sttProvider || 'azure'}
            onChange={(e) => handleProviderChange(e.target.value)}
            disabled={isRecording}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          >
            <option value="azure">Azure Speech (Cloud)</option>
            <option value="speechmatics">Speechmatics (Cloud)</option>
            <option value="sherpa">Sherpa ONNX (Local GPU)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Mode
          </label>
          <div className="flex gap-2">
            {[
              { value: 'realtime', label: 'Realtime' },
              { value: 'batch', label: 'Batch' },
            ].map((option) => {
              const isSelected = mode === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleModeChange(option.value)}
                  disabled={isRecording}
                  title={
                    option.value === 'realtime'
                      ? 'Stream audio to server as you speak'
                      : 'Record locally and upload full file'
                  }
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <p className="text-xs text-gray-500 pt-1 border-t border-gray-100">
        {mode === 'realtime'
          ? 'Live streaming via ' +
            (clientSettings.sttProvider === 'sherpa' ? 'local GPU' : 'cloud API')
          : 'Record audio locally, then process'}
      </p>
    </div>
  );

  const renderRecordingCard = () => {
    return (
      <div
        className={`card space-y-3 ${isMobile ? 'sticky top-0 z-30 bg-white/95 backdrop-blur border border-gray-200 shadow-lg' : ''
          }`}
      >
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <MicIcon />
          Recording
        </h3>
        <button
          onClick={isRecording ? stopRecording : startRecording}
          disabled={isProcessing}
          title={isRecording ? 'Stop recording' : 'Start recording'}
          className={`btn w-full justify-center text-base ${isRecording ? 'btn-danger animate-pulse' : 'btn-primary'
            }`}
        >
          {isRecording ? (
            <>
              <StopIcon />
              Stop Recording
            </>
          ) : (
            <>
              <MicIcon />
              Start Recording{isMobile ? '' : ' (Ctrl+D)'}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={handleCountdownReset}
          title="Reset the recording timer"
          className={`btn btn-secondary w-full justify-center text-sm ${isRecording ? 'bg-amber-50 border-amber-300 text-amber-800' : ''
            }`}
        >
          ⏱ {formatCountdown(remainingTime)}
        </button>
        <p className="text-xs text-gray-500 text-center">
          Timer resets to {DEFAULT_RECORDING_SECONDS}s when clicked
        </p>

        {isSilencePaused && (
          <p className="text-sm text-amber-600 text-center">
            {silencePauseMessage || 'Recording paused after silence. Start again to continue.'}
          </p>
        )}

        {isRecording && (
          <div className="space-y-2 pt-2">
            <div className="flex justify-between text-sm text-gray-600">
              <span>Input Level</span>
              <span>{Math.round(inputLevel * 100)}%</span>
            </div>
            <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-green-600 transition-all duration-100"
                style={{ width: `${inputLevel * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderPromptsCard = () => (
    <div className="card space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <DocumentIcon />
        Quick Prompts
      </h3>
      {prompts.length > 0 ? (
        <div className="space-y-2">
          {prompts.map((prompt) => (
            <button
              key={prompt.id}
              type="button"
              className="w-full text-left border-2 border-gray-200 rounded-lg p-3 hover:border-primary-500 hover:bg-primary-50 transition"
              onClick={() => {
                setTranscriptionText((prev) => `${prev} ${prompt.text}`.trim());
                toast.success('Prompt inserted');
              }}
            >
              <p className="font-medium text-gray-900">{prompt.name}</p>
              <p className="text-sm text-gray-600 mt-1">{prompt.text}</p>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-4 text-gray-500 text-sm">No prompts yet. Create one!</div>
      )}
    </div>
  );

  const renderStatsCard = () => (
    <div className="card space-y-3">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <ChartIcon />
        Usage Stats
      </h3>
      {stats && (
        <div className="text-sm text-gray-700 space-y-2">
          <div className="flex justify-between">
            <span className="text-gray-500">Sessions:</span>
            <span className="font-semibold">{stats.total_sessions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Words:</span>
            <span className="font-semibold">{stats.total_words.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Realtime:</span>
            <span className="font-semibold">{stats.realtime_sessions}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Batch:</span>
            <span className="font-semibold">{stats.batch_sessions}</span>
          </div>
        </div>
      )}

      <CostTracker
        sessionCost={sessionCost}
        totalCost={totalCost}
        mode={mode}
        isRecording={isRecording}
        ratePerHour={providerRates[mode] ?? providerRates.realtime}
        provider={currentProvider}
      />
    </div>
  );

  const renderTranscriptionSection = () => (
    <div className="space-y-4">
      <div className="card space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <DocumentIcon />
            Transcription Output
          </h2>
          <div className="flex flex-wrap gap-2 w-full md:w-auto">
            <button
              onClick={() => {
                const sentences = transcriptionText.match(/[^.!?]+[.!?]+/g);
                if (sentences && sentences.length > 0) {
                  const latest = sentences[sentences.length - 1].trim();
                  navigator.clipboard.writeText(latest);
                  toast.success('Latest sentence copied');
                }
              }}
              title="Copy the most recent sentence"
              className="btn btn-secondary text-sm"
              disabled={!transcriptionText}
            >
              ✂️ Sentence
            </button>
            <button
              onClick={openAiCorrectionDialog}
              title="Open AI correction dialog"
              className="btn btn-primary text-sm"
              disabled={isCorrecting || !transcriptionText}
            >
              ✨ AI Correct
            </button>
            <button
              onClick={() => {
                copyTranscriptionToClipboard({
                  successMessage: 'Copied to clipboard',
                });
              }}
              title="Copy output to clipboard"
              className="btn btn-secondary text-sm"
              disabled={!transcriptionText}
            >
              📋 Copy
            </button>
            <button
              onClick={handleOpenToolbar}
              title={
                isDocPipAvailable
                  ? 'Open floating always-on-top toolbar'
                  : 'Document Picture-in-Picture is not supported in this browser'
              }
              className="btn btn-secondary text-sm"
              disabled={!isDocPipAvailable}
            >
              🪟 Toolbar
            </button>
            <button
              onClick={() => {
                setTranscriptionText('');
                setConfidence(null);
                toast.success('Transcription cleared');
              }}
              title="Clear the current transcription"
              className="btn btn-secondary text-sm"
              disabled={!transcriptionText}
            >
              🗑️ Clear
            </button>
          </div>
        </div>

        <textarea
          ref={transcriptionTextareaRef}
          data-cipher-transcription="true"
          value={transcriptionText}
          onChange={(e) => setTranscriptionText(e.target.value, { coalesceKey: 'typing' })}
          placeholder="Transcribed text will appear here... Start recording or insert a prompt."
          className="input-field font-mono min-h-[220px] md:min-h-[400px]"
        />

        <div className="flex flex-col md:flex-row md:items-center md:justify-between text-sm text-gray-700 gap-2">
          <div className="flex flex-wrap gap-4">
            <span>
              Words: {transcriptionText.split(/\s+/).filter((w) => w).length}
            </span>
            <span>Characters: {transcriptionText.length}</span>
            <span>
              Provider: {currentProvider === 'sherpa' ? 'Sherpa ONNX' : 'Azure Speech'}
            </span>
            {confidence !== null && (
              <span>Confidence: {(confidence * 100).toFixed(1)}%</span>
            )}
          </div>
          <div className="text-xs text-gray-500 space-x-2 flex flex-wrap items-center">
            <span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+D</kbd> = Start/Stop
            </span>
            <span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+K</kbd> = Correct
            </span>
            <span>
              <kbd className="px-2 py-1 bg-gray-100 rounded">Ctrl+C</kbd> = Copy
            </span>
          </div>
        </div>
      </div>

      {isProcessing && (
        <div className="card flex items-center gap-3 border-blue-100 bg-blue-50">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-blue-800 font-medium">Processing audio...</span>
        </div>
      )}
      {isCorrecting && (
        <div className="card flex items-center gap-3 border-amber-200 bg-amber-50">
          <div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-amber-800 font-medium">Applying AI correction...</span>
        </div>
      )}
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        <div className="card">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
                <MicIcon />
                Wyrm - Clinical Dictation
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Clinical Input Processing Health Entry Recorder
              </p>
            </div>
            <div className="text-sm text-gray-600 text-left md:text-right space-y-1">
              <p>Logged in as</p>
              <p className="font-semibold text-gray-900">{user?.name || 'Developer'}</p>
              {syncStatusLabel && (
                <p className={`text-xs ${syncStatusClass}`}>{syncStatusLabel}</p>
              )}
            </div>
          </div>
        </div>

        {isMobile ? (
          <div className="space-y-4">
            {renderRecordingCard()}
            {renderTranscriptionSection()}
            {renderConfigCard()}
            {renderPromptsCard()}
            {renderStatsCard()}
          </div>
        ) : (
          <div className="grid gap-5 lg:grid-cols-[340px_1fr]">
            <div className="space-y-5">
              {renderConfigCard()}
              {renderRecordingCard()}
              {renderPromptsCard()}
              {renderStatsCard()}
            </div>
            {renderTranscriptionSection()}
          </div>
        )}
      </div>

      {isCorrectionDialogOpen && (
        <div
          className="fixed inset-0 bg-gray-900/60 flex items-center justify-center p-4 z-50"
          onClick={() => {
            if (!isCorrecting) {
              closeAiCorrectionDialog();
            }
          }}
        >
          <div
            className="bg-white rounded-xl w-full max-w-lg p-6 shadow-xl space-y-4"
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <h3 className="text-lg font-semibold text-gray-900">AI correction instructions</h3>
            <p className="text-sm text-gray-600">
              Provide optional guidance for the AI (e.g. "Please tidy up this neurology referral letter").
            </p>
            <textarea
              ref={instructionInputRef}
              value={instructionDraft}
              onChange={(event) => setInstructionDraft(event.target.value)}
              placeholder="Add instructions for the AI (optional)..."
              className="input-field min-h-[120px]"
              onKeyDown={(event) => {
                if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                  event.preventDefault();
                  handleConfirmCorrection();
                }
              }}
            />
            <p className="text-xs text-gray-500">Press Ctrl+Enter (or Cmd+Enter) to apply instantly.</p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={closeAiCorrectionDialog}
                className="btn btn-secondary text-sm"
                disabled={isCorrecting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmCorrection}
                className="btn btn-primary text-sm"
                disabled={isCorrecting}
              >
                {isCorrecting ? 'Applying...' : 'Apply'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

};

export default CipherPage;
